/**
 * Utility function to create a fixed route from KML stops
 * Parses KML placemarks and creates stops and a route
 */

import { v4 as uuidv4 } from 'uuid';
import type { FixedRouteStop, FixedRoute, FixedRouteStopConfig, FixedRouteSegment } from '../../types/fixedRoutes';
import { generateRouteColor } from './routeToLineConverter';

/**
 * Parse KML XML and extract stops
 */
export function parseKMLStops(kmlContent: string): Array<{ name: string; lat: number; lng: number }> {
  const parser = new DOMParser();
  const doc = parser.parseFromString(kmlContent, 'text/xml');
  
  // Check for parsing errors
  const parserError = doc.querySelector('parsererror');
  if (parserError) {
    throw new Error('Failed to parse KML: ' + parserError.textContent);
  }
  
  const placemarks = doc.querySelectorAll('Placemark');
  const stops: Array<{ name: string; lat: number; lng: number }> = [];
  
  placemarks.forEach((placemark) => {
    const nameElement = placemark.querySelector('name');
    const coordinatesElement = placemark.querySelector('coordinates');
    
    if (nameElement && coordinatesElement) {
      const name = nameElement.textContent?.trim() || 'Unnamed Stop';
      const coordsText = coordinatesElement.textContent?.trim();
      
      if (coordsText) {
        // KML format: longitude,latitude,altitude (comma-separated)
        const parts = coordsText.split(',');
        if (parts.length >= 2) {
          const lng = parseFloat(parts[0]);
          const lat = parseFloat(parts[1]);
          
          if (!isNaN(lat) && !isNaN(lng)) {
            stops.push({ name, lat, lng });
          }
        }
      }
    }
  });
  
  return stops;
}

/**
 * Create FixedRouteStop objects from parsed KML stops
 */
export function createStopsFromKML(kmlStops: Array<{ name: string; lat: number; lng: number }>): FixedRouteStop[] {
  return kmlStops.map((stop) => ({
    id: uuidv4(),
    name: stop.name,
    coordinates: {
      lat: stop.lat,
      lng: stop.lng,
    },
  }));
}

/**
 * Initialize waypoints from stops
 * Creates waypoints array from stop coordinates
 */
export function initializeWaypointsFromStops(stops: FixedRouteStop[]): [number, number][] {
  return stops
    .filter(stop => stop.coordinates && stop.coordinates.lat !== undefined && stop.coordinates.lng !== undefined)
    .map(stop => [stop.coordinates!.lat, stop.coordinates!.lng] as [number, number]);
}

/**
 * Create a fixed route from stops
 */
export function createRouteFromStops(
  stops: FixedRouteStop[],
  routeName: string = 'New Route',
  startTime: string = '08:00',
  waitTimeMinutes: number = 2
): FixedRoute {
  if (stops.length < 2) {
    throw new Error('A route must have at least 2 stops');
  }
  
  const routeId = uuidv4();
  
  // Create stop configs (ordered list with wait times)
  const stopConfigs: FixedRouteStopConfig[] = stops.map((stop) => ({
    stopId: stop.id,
    waitTimeMinutes,
  }));
  
  // Create segments (will be calculated later by the routing service)
  const segments: FixedRouteSegment[] = [];
  for (let i = 0; i < stops.length - 1; i++) {
    segments.push({
      fromStopId: stops[i].id,
      toStopId: stops[i + 1].id,
      travelTimeMinutes: 0, // Will be calculated
    });
  }
  
  // Initialize waypoints from stops
  const waypoints = initializeWaypointsFromStops(stops);
  
  return {
    id: routeId,
    name: routeName,
    startTime,
    stops: stopConfigs,
    segments,
    waypoints, // Initialize waypoints from stops
    color: generateRouteColor(routeId),
    width: 6,
  };
}

/**
 * Complete function: Parse KML and create stops + route
 */
export function createRouteFromKML(
  kmlContent: string,
  routeName: string = 'Imported Route'
): { stops: FixedRouteStop[]; route: FixedRoute } {
  const kmlStops = parseKMLStops(kmlContent);
  const stops = createStopsFromKML(kmlStops);
  const route = createRouteFromStops(stops, routeName);
  
  return { stops, route };
}

